// src/modules/tenant/AssignEventModal.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Search,
  X,
} from "lucide-react";
import { API_ROOT_URL } from "../../config/api";

interface AssignableAsset {
  id: string;
  original_filename: string;
}

interface TreeEvent {
  event_id: string;
  title: string;
  status?: string | null;
}

interface TreeCampaign {
  campaign_id: string;
  title: string;
  status?: string | null;
  events: TreeEvent[];
}

interface AssignEventModalProps {
  selectedAssets: AssignableAsset[];
  onClose: () => void;
  onAssigned: () => void;
}

// Same event can sit under several campaigns, so selection is per (campaign, event)
const keyOf = (campaignId: string, eventId: string) => `${campaignId}|${eventId}`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("accessToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getTenantId = (): string => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.tenantId ? String(u.tenantId) : "";
  } catch {
    return "";
  }
};

// Bold the part of the text that matches the search
const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const i = query ? text.toLowerCase().indexOf(query) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-yellow-100 text-inherit rounded px-0.5">
        {text.slice(i, i + query.length)}
      </mark>
      {text.slice(i + query.length)}
    </>
  );
};

const AssignEventModal: React.FC<AssignEventModalProps> = ({
  selectedAssets,
  onClose,
  onAssigned,
}) => {
  const [campaigns, setCampaigns] = useState<TreeCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [eventKeys, setEventKeys] = useState<Set<string>>(new Set());
  const [campaignOnly, setCampaignOnly] = useState<Set<string>>(new Set());
  const [showFiles, setShowFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const tenantId = getTenantId();
      const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
      const res = await fetch(`${API_ROOT_URL}/asset-links/tree${qs}`, {
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || `Request failed with ${res.status}`);
      setCampaigns(
        (body.data?.campaigns || []).map((c: any) => ({
          campaign_id: String(c.campaign_id),
          title: c.title,
          status: c.status,
          events: (c.events || []).map((e: any) => ({
            event_id: String(e.event_id),
            title: e.title,
            status: e.status,
          })),
        })),
      );
    } catch (err: any) {
      setLoadError(err?.message || "Could not load campaigns and events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);

  // Campaign title match keeps all its events; otherwise keep only matching events
  const visibleCampaigns = useMemo(
    () =>
      campaigns
        .map((c) =>
          matches(c.title) ? c : { ...c, events: c.events.filter((e) => matches(e.title)) },
        )
        .filter((c) => matches(c.title) || c.events.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaigns, q],
  );

  const campaignTitle = new Map(campaigns.map((c) => [c.campaign_id, c.title]));
  const eventsPickedIn = (cid: string) =>
    Array.from(eventKeys).filter((k) => k.startsWith(`${cid}|`)).length;
  const isCampaignChecked = (cid: string) =>
    campaignOnly.has(cid) || eventsPickedIn(cid) > 0;

  const clearCampaign = (cid: string) => {
    setCampaignOnly((prev) => {
      const next = new Set(prev);
      next.delete(cid);
      return next;
    });
    setEventKeys((prev) => new Set(Array.from(prev).filter((k) => !k.startsWith(`${cid}|`))));
  };

  const toggleCampaign = (cid: string) => {
    if (isCampaignChecked(cid)) clearCampaign(cid);
    else setCampaignOnly((prev) => new Set(prev).add(cid));
  };

  const toggleEvent = (cid: string, eid: string) => {
    const key = keyOf(cid, eid);
    setEventKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCollapsed = (cid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });

  const allCollapsed = campaigns.length > 0 && collapsed.size >= campaigns.length;
  const toggleAllCollapsed = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(campaigns.map((c) => c.campaign_id)));

  const selectedCampaignIds = Array.from(
    new Set<string>([
      ...Array.from(campaignOnly),
      ...Array.from(eventKeys).map((k) => k.split("|")[0]),
    ]),
  );
  const nothingSelected = selectedCampaignIds.length === 0;

  const handleAssign = async () => {
    if (nothingSelected || selectedAssets.length === 0) return;
    setSubmitting(true);
    const toastId = toast.loading("Assigning assets...");
    try {
      const res = await fetch(`${API_ROOT_URL}/asset-links/assign`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          tenant_id: getTenantId() || undefined,
          asset_ids: selectedAssets.map((a) => a.id),
          campaign_ids: Array.from(campaignOnly),
          event_links: Array.from(eventKeys).map((k) => {
            const [campaign_id, event_id] = k.split("|");
            return { event_id, campaign_id };
          }),
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || `Request failed with ${res.status}`);

      const names = selectedCampaignIds.map((id) => campaignTitle.get(id)).filter(Boolean);
      toast.update(toastId, {
        render: `${plural(selectedAssets.length, "asset")} linked to ${names.join(", ")}`,
        type: "success",
        isLoading: false,
        autoClose: 3500,
      });
      onAssigned();
    } catch (err: any) {
      toast.update(toastId, {
        render: err?.message || "Failed to assign assets",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderEventRow = (cid: string, ev: TreeEvent) => {
    const checked = eventKeys.has(keyOf(cid, ev.event_id));
    return (
      <label
        key={keyOf(cid, ev.event_id)}
        className={`flex items-center gap-2.5 pl-12 pr-3 py-2 text-sm cursor-pointer transition-colors ${
          checked ? "bg-purple-50" : "hover:bg-gray-50"
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggleEvent(cid, ev.event_id)}
          className="h-4 w-4 accent-purple-600 cursor-pointer shrink-0"
        />
        <Calendar size={14} className="text-gray-400 shrink-0" />
        <span className={`truncate ${checked ? "text-purple-700 font-medium" : "text-gray-700"}`}>
          <Highlight text={ev.title} query={q} />
        </span>
        {ev.status && (
          <span className="ml-auto text-[10px] uppercase tracking-wide text-gray-400 shrink-0">
            {ev.status}
          </span>
        )}
      </label>
    );
  };

  const renderTree = () => {
    if (loading) {
      return (
        <div className="p-4 space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2 animate-pulse">
              <div className="h-4 w-1/2 bg-gray-200 rounded" />
              <div className="h-3 w-1/3 bg-gray-100 rounded ml-8" />
              <div className="h-3 w-2/5 bg-gray-100 rounded ml-8" />
            </div>
          ))}
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="p-6 text-center">
          <p className="text-sm text-red-600">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      );
    }
    if (campaigns.length === 0) {
      return (
        <div className="p-6 text-center">
          <FolderKanban size={28} className="mx-auto text-gray-300" />
          <p className="mt-2 text-sm font-medium text-gray-700">No campaigns yet</p>
          <p className="mt-1 text-xs text-gray-500">
            Create a campaign and add events to it under Campaigns &amp; Events, then come back here.
          </p>
        </div>
      );
    }
    if (visibleCampaigns.length === 0) {
      return (
        <div className="p-6 text-center">
          <p className="text-sm text-gray-500">
            No campaigns or events match "{query.trim()}"
          </p>
          <button
            type="button"
            onClick={() => setQuery("")}
            className="mt-2 text-sm text-purple-600 hover:underline"
          >
            Clear search
          </button>
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-100">
        {visibleCampaigns.map((c) => {
          const open = q ? true : !collapsed.has(c.campaign_id);
          const checked = isCampaignChecked(c.campaign_id);
          const viaEvents = eventsPickedIn(c.campaign_id);
          return (
            <div key={c.campaign_id}>
              <div
                className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${
                  checked ? "bg-purple-50/60" : "hover:bg-gray-50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleCollapsed(c.campaign_id)}
                  disabled={!!q || c.events.length === 0}
                  className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label={open ? "Collapse" : "Expand"}
                  aria-expanded={open}
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <label className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCampaign(c.campaign_id)}
                    className="h-4 w-4 accent-purple-600 cursor-pointer shrink-0"
                  />
                  <FolderKanban size={15} className="text-purple-500 shrink-0" />
                  <span className="truncate text-sm font-semibold text-gray-800">
                    <Highlight text={c.title} query={q} />
                  </span>
                  {viaEvents > 0 && (
                    <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      via {plural(viaEvents, "event")}
                    </span>
                  )}
                </label>
                <span className="text-xs text-gray-400 shrink-0">
                  {plural(c.events.length, "event")}
                </span>
              </div>
              {open && c.events.map((ev) => renderEventRow(c.campaign_id, ev))}
              {open && c.events.length === 0 && (
                <p className="pl-12 pr-3 pb-2.5 text-xs text-gray-400">No events in this campaign yet</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-event-title"
        className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[88vh]"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 id="assign-event-title" className="text-lg font-semibold text-gray-800">
              Assign to campaign / event
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {plural(selectedAssets.length, "asset")} selected
              {" · "}
              <button
                type="button"
                onClick={() => setShowFiles((s) => !s)}
                className="text-purple-600 hover:underline"
              >
                {showFiles ? "Hide files" : "Show files"}
              </button>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {showFiles && (
          <ul className="mx-6 mt-3 max-h-24 overflow-y-auto text-xs text-gray-600 bg-gray-50 rounded-lg p-2 space-y-1">
            {selectedAssets.map((a) => (
              <li key={a.id} className="truncate">
                {a.original_filename}
              </li>
            ))}
          </ul>
        )}

        {/* Search + helper */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search campaigns or events..."
              className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>Tick an event and its campaign is selected for you.</span>
            {!q && campaigns.length > 1 && (
              <button
                type="button"
                onClick={toggleAllCollapsed}
                className="text-purple-600 hover:underline shrink-0 ml-3"
              >
                {allCollapsed ? "Expand all" : "Collapse all"}
              </button>
            )}
          </div>
        </div>

        {/* Tree */}
        <div className="mx-6 mb-4 border border-gray-200 rounded-lg overflow-y-auto flex-1 min-h-[220px]">
          {renderTree()}
        </div>

        {/* Selection summary + actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-3">
          <div className="text-sm">
            {nothingSelected ? (
              <span className="text-gray-500">Select an event or campaign above.</span>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-gray-600 mr-1">Will be linked to:</span>
                {selectedCampaignIds.map((cid) => (
                  <span
                    key={cid}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-white border border-purple-200 text-purple-700 text-xs font-medium"
                  >
                    {campaignTitle.get(cid) || cid}
                    <button
                      type="button"
                      onClick={() => clearCampaign(cid)}
                      className="p-0.5 rounded-full hover:bg-purple-100"
                      aria-label={`Remove ${campaignTitle.get(cid) || "campaign"}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={submitting || nothingSelected}
              className="px-4 py-2 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700 transition disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {submitting
                ? "Assigning..."
                : nothingSelected
                  ? "Assign"
                  : `Assign to ${plural(selectedCampaignIds.length, "campaign")}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignEventModal;